import cx from 'classnames';
import Linkify from 'linkify-react';
import _ from 'lodash';
import {
  ElementType,
  forwardRef,
  HTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  MouseEvent,
  ReactNode,
  useId,
  useRef,
} from 'react';
import { Form as BootstrapForm, FormControlProps } from 'react-bootstrap';
import { AsyncSelect } from './AsyncSelect';
import { Checkbox } from './Checkbox';
import {
  S3MultiFileUpload,
  S3SingleFileUpload,
  SingleFileUpload,
} from './FileUpload';
import css from './FormV2.module.scss';
import { Radio } from './Radio';
import { RichText } from './RichText';
import { Select } from './Select';
import { Toggle } from './Toggle';

export type ControlNamespace = typeof Control & {
  Feedback: typeof BootstrapForm.Control.Feedback;
};

export type ControlProps = FormControlProps &
  InputHTMLAttributes<HTMLInputElement> & {
    errorMessage?: string;
    errorMessageKey?: string | number;
    icon?: ElementType;
    iconPosition?: 'left' | 'right';
    iconSize?: number;
    unitsText?: string;
    wrapperClassName?: string;
  };

const Control = forwardRef<typeof BootstrapForm.Control, ControlProps>(
  (
    {
      errorMessage,
      errorMessageKey,
      children,
      className,
      icon: Icon,
      iconPosition = 'left',
      iconSize = 13,
      unitsText,
      wrapperClassName,
      ...rest
    },
    ref
  ) => {
    const errorMessageId = useId();
    return !!Icon ? (
      <div className={cx(css.iconControlWrapper, wrapperClassName)}>
        <Icon
          className={cx('ethena-icon', css.icon, {
            [css.right]: iconPosition === 'right',
          })}
          height={iconSize}
          width={iconSize}
        />
        <BootstrapForm.Control
          aria-describedby={errorMessageId}
          className={cx(css.control, className, {
            [css.right]: iconPosition === 'right',
          })}
          ref={ref}
          {...rest}
        >
          {children}
        </BootstrapForm.Control>
        <BootstrapForm.Control.Feedback
          key={errorMessageKey}
          id={errorMessageId}
          type="invalid"
        >
          {errorMessage}
        </BootstrapForm.Control.Feedback>
      </div>
    ) : (
      <div className={cx(css.controlWrapper, wrapperClassName)}>
        <BootstrapForm.Control
          aria-describedby={errorMessageId}
          className={cx(css.control, className)}
          ref={ref}
          {...rest}
        >
          {children}
        </BootstrapForm.Control>
        {unitsText && <div className={css.units}>{unitsText}</div>}
        <BootstrapForm.Control.Feedback
          key={errorMessageKey}
          id={errorMessageId}
          type="invalid"
        >
          {errorMessage}
        </BootstrapForm.Control.Feedback>
      </div>
    );
  }
);

Control.displayName = 'Control';
(Control as ControlNamespace).Feedback = BootstrapForm.Control.Feedback;

type Color = 'black' | 'gray';

interface IDetailsProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
  color?: Color;
  fontSize?: number;
  variant?: 'light' | 'regular';
}

const Details = ({
  as: Component = 'p',
  className,
  color: passedColor = 'black',
  fontSize: passedFontSize = 16,
  variant = 'regular',
  ...rest
}: IDetailsProps) => {
  let color: Color;
  let fontSize: number;

  switch (variant) {
    case 'light':
      color = 'gray';
      fontSize = 14;
      break;
    default:
      color = passedColor;
      fontSize = passedFontSize;
      break;
  }

  return (
    <Component
      className={cx(css.details, css[color], className)}
      style={{ fontSize }}
      {...rest}
    />
  );
};

interface IGroupProps extends HTMLAttributes<HTMLDivElement> {
  as?: ElementType;
  isHorizontal?: boolean;
}

const Group = ({
  as: Component = 'div',
  className,
  isHorizontal,
  ...rest
}: IGroupProps) => (
  <Component
    className={cx(className, css.group, {
      [css.horizontal]: isHorizontal,
    })}
    {...rest}
  />
);

export interface ILabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  isDisabled?: boolean;
  required?: boolean;
}

const Label = ({
  className,
  children,
  required,
  isDisabled = false,
  ...rest
}: ILabelProps) => (
  <label
    className={cx(css.label, className, { [css.disabled]: isDisabled })}
    {...rest}
  >
    {children} {required && <span className={css.asterisk}>*</span>}
  </label>
);

export type TextareaProps = Omit<FormControlProps, 'isInvalid'> &
  InputHTMLAttributes<HTMLInputElement> & {
    errorMessage?: string;
    hideMaxLength?: boolean;
    isInvalid?: boolean;
    label?: string;
    rows?: number;
    value: string;
  };

const Textarea = ({
  className,
  hideMaxLength,
  label,
  value,
  ...rest
}: TextareaProps) => {
  const { current: id } = useRef(_.uniqueId('textarea'));
  return (
    <div className={cx(css.textareaWrapper, className)}>
      <header>
        {!!label && <label htmlFor={id}>{label}</label>}
        {!!rest.maxLength && !hideMaxLength && (
          <p className={css.length}>
            {value?.length || 0}/{rest.maxLength}
          </p>
        )}
      </header>
      <Control as="textarea" value={value} {...rest} id={id} />
    </div>
  );
};

export interface ITextareaTextProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
  children: ReactNode;
  linkClassName?: string;
}

/**
 * Takes text from a textarea and displays it nicely.
 */
const TextareaText = ({
  as: Component = 'p',
  children,
  className,
  linkClassName,
  ...rest
}: ITextareaTextProps) => {
  return (
    <Component className={cx(css.textareaText, className)} {...rest}>
      <Linkify
        options={{
          className: linkClassName,
          attributes: {
            onClick: (e: MouseEvent) => e.stopPropagation(),
          },
          target: '_blank',
        }}
        {...rest}
      >
        {children}
      </Linkify>
    </Component>
  );
};

export const FormV2 = {
  ...BootstrapForm,
  AsyncSelect,
  Checkbox,
  Control,
  Details,
  Group,
  Label,
  Radio,
  RichText,
  S3MultiFileUpload,
  S3SingleFileUpload,
  Select,
  SingleFileUpload,
  Textarea,
  TextareaText,
  Toggle,
} as typeof BootstrapForm & {
  AsyncSelect: typeof AsyncSelect;
  Checkbox: typeof Checkbox;
  Control: any; // TODO: Fix this type
  Details: typeof Details;
  Group: typeof Group;
  Label: typeof Label;
  Radio: typeof Radio;
  RichText: typeof RichText;
  S3MultiFileUpload: typeof S3MultiFileUpload;
  S3SingleFileUpload: typeof S3SingleFileUpload;
  Select: any; // TODO: Fix this type
  SingleFileUpload: typeof SingleFileUpload;
  Textarea: any; // TODO: Fix this type
  TextareaText: any; // TODO: Fix this type
  Toggle: typeof Toggle;
};
